import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { motion } from "framer-motion";
import { Library } from "lucide-react";

import { Modal } from "../../../../shared/ui/modal";
import MeditationIcon from "../../../../shared/ui/icons/MeditationIcon";

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

/**
 * What the Meditación dock entry opens.
 *
 * The same shape as the Pagos and Agendar sheets — a therapist who has learned
 * one dock has learned the others — and, like Pagos, it fetches nothing: both
 * actions are navigations, and the pages they land on load what they need.
 *
 * No patient is asked for. A meditation belongs to the therapist's library
 * rather than to one patient, and is chosen for someone inside the session.
 */
export default function MeditationDockActions({ isOpen, onClose }: Props) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const go = (path: string) => () => {
        onClose();
        navigate(path);
    };

    const entry = (
        onClick: () => void,
        icon: React.ReactNode,
        title: string,
        hint: string,
        primary = false
    ) => (
        <button
            type="button"
            onClick={onClick}
            className={`mb-4 flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition-colors ${
                primary
                    ? "border-brand-100 bg-brand-50 text-brand-700 hover:bg-brand-100 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-300"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800/50"
            }`}
        >
            <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                    primary
                        ? "bg-white text-brand-600 shadow-sm dark:bg-gray-800"
                        : "bg-gray-100 text-gray-500 dark:bg-gray-800"
                }`}
            >
                {icon}
            </span>
            <span className="flex-1">
                <span className="block text-sm font-semibold">{title}</span>
                <span className="block text-xs opacity-80">{hint}</span>
            </span>
        </button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            showCloseButton={false}
            className="mx-4 max-w-lg rounded-3xl"
        >
            <motion.div
                initial={{ y: 24, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                className="flex max-h-[80vh] flex-col p-5"
            >
                <div className="mb-4">
                    <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                        {t("meditation.launchSheet.title")}
                    </h2>
                    <p className="text-sm text-content-tertiary">
                        {t("meditation.launchSheet.subtitle")}
                    </p>
                </div>

                {entry(
                    go("/meditations/new"),
                    <MeditationIcon className="h-5 w-5" size={20} />,
                    t("meditation.launchSheet.new"),
                    t("meditation.launchSheet.newHint"),
                    true
                )}

                {entry(
                    go("/meditations"),
                    <Library className="h-5 w-5" />,
                    t("meditation.launchSheet.library"),
                    t("meditation.launchSheet.libraryHint")
                )}

                <button
                    type="button"
                    onClick={onClose}
                    className="mt-2 w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800/50"
                >
                    {t("common.cancel")}
                </button>
            </motion.div>
        </Modal>
    );
}
